import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  Users, TrendingUp, Mail, CheckCircle2, Clock,
  RefreshCw, Loader2, ArrowRight, BarChart3, Target,
  Eye, MousePointer, Send
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const TIER_LABELS = {
  inner_circle: 'Inner Circle',
  inner_edge: 'Inner Edge',
  coaching_activation: 'Coaching Activation',
  sdc: 'Collective',
  costa_rica: 'Retreat',
  pathways: 'Pathways',
};

const AdminFunnelDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [backfilling, setBackfilling] = useState(false);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/funnel/dashboard`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch');
      setData(await res.json());
    } catch {
      toast.error('Failed to load funnel dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleBackfillOntraport = async () => {
    if (!window.confirm(
      'Push every historical Diamond Reset lead into Ontraport and apply the "My Life Cost" tag (494)?\n\n' +
      'This is idempotent — already-synced leads are skipped, and Ontraport tolerates duplicates.'
    )) return;

    setBackfilling(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/funnel/backfill-ontraport`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.detail || 'Backfill failed');
      toast.success(
        `Backfill complete — synced ${body.synced}, skipped ${body.already_synced_skipped}, failed ${body.failed} of ${body.total}.`,
        { duration: 10000 }
      );
      if (body.failed > 0) {
        const sample = (body.errors || []).slice(0, 3).map(e => `• ${e.email}: ${e.error}`).join('\n');
        toast.error(`${body.failed} lead(s) failed. First few:\n${sample}`, { duration: 14000 });
      }
      fetchDashboard();
    } catch (e) {
      toast.error(e.message || 'Ontraport backfill failed');
    } finally {
      setBackfilling(false);
    }
  };

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-[#A86D53]" />
      </div>
    );
  }

  if (!data) return null;

  const maxDaily = Math.max(...(data.daily_optins?.map(d => d.count) || [1]), 1);

  return (
    <div className="space-y-6" data-testid="admin-funnel-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-[#2A231E]">Diamond Reset Funnel</h2>
          <p className="text-sm text-gray-500 mt-1">Lead capture & email sequence performance</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchDashboard} disabled={loading} data-testid="funnel-refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={handleBackfillOntraport}
            disabled={backfilling}
            style={{ backgroundColor: '#C06C54', color: '#ffffff' }}
            data-testid="funnel-backfill-ontraport-btn"
            title='Push every historical lead into Ontraport with the "My Life Cost" tag (idempotent)'
          >
            {backfilling ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            Backfill to Ontraport
          </Button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <StatCard icon={Users} label="Total Leads" value={data.total_leads} color="text-[#A86D53]" bg="bg-[#A86D53]/5 border-[#A86D53]/20" testId="stat-total-leads" />
        <StatCard icon={TrendingUp} label="Today" value={data.today_leads} color="text-emerald-600" bg="bg-emerald-50 border-emerald-200" testId="stat-today-leads" />
        <StatCard icon={BarChart3} label="This Week" value={data.this_week_leads} color="text-blue-600" bg="bg-blue-50 border-blue-200" testId="stat-week-leads" />
        <StatCard icon={Target} label="Converted" value={data.conversion?.total_converted || 0} sub={`${data.conversion?.conversion_rate || 0}%`} color="text-emerald-700" bg="bg-emerald-50 border-emerald-300" testId="stat-converted" />
        <StatCard icon={Eye} label="Open Rate" value={`${data.engagement?.overall_open_rate || 0}%`} sub={`${data.engagement?.total_opens || 0} opens`} color="text-indigo-600" bg="bg-indigo-50 border-indigo-200" testId="stat-open-rate" />
        <StatCard icon={MousePointer} label="Click Rate" value={`${data.engagement?.overall_click_rate || 0}%`} sub={`${data.engagement?.total_clicks || 0} clicks`} color="text-amber-600" bg="bg-amber-50 border-amber-200" testId="stat-click-rate" />
        <StatCard icon={CheckCircle2} label="Completed Seq." value={data.completed_sequence} color="text-purple-600" bg="bg-purple-50 border-purple-200" testId="stat-completed-seq" />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Email Sequence Progress */}
        <Card data-testid="email-sequence-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Mail className="h-4 w-4 text-[#A86D53]" />
              Email Sequence Progress
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {data.email_progress?.map((em) => {
              const pct = data.total_leads > 0 ? Math.round((em.sent_count / data.total_leads) * 100) : 0;
              return (
                <div key={em.index} className="space-y-1" data-testid={`email-step-${em.index}`}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 truncate flex-1 mr-2">
                      <span className="font-medium text-[#2A231E]">Day {em.day}</span>
                      <span className="text-gray-400 mx-1.5">|</span>
                      {em.subject}
                    </span>
                    <span className="font-medium text-[#2A231E] whitespace-nowrap">{em.sent_count}</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${pct}%`,
                        backgroundColor: em.index === 1 ? '#A86D53' : em.index <= 4 ? '#C4956C' : '#D4B89C',
                      }}
                    />
                  </div>
                  {(em.opens > 0 || em.clicks > 0) && (
                    <div className="flex items-center gap-3 text-xs text-gray-400 pl-1">
                      <span className="flex items-center gap-1">
                        <Eye className="h-3 w-3" /> {em.open_rate}% opened
                      </span>
                      <span className="flex items-center gap-1">
                        <MousePointer className="h-3 w-3" /> {em.click_rate}% clicked
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Daily Opt-ins Chart */}
        <Card data-testid="daily-optins-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-[#A86D53]" />
              Daily Opt-ins (Last 30 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.daily_optins?.length > 0 ? (
              <div className="flex items-end gap-[3px] h-40">
                {data.daily_optins.map((d, i) => {
                  const h = Math.max((d.count / maxDaily) * 100, 4);
                  return (
                    <div
                      key={d.date}
                      className="flex-1 group relative"
                      data-testid={`daily-bar-${d.date}`}
                    >
                      <div
                        className="w-full bg-[#A86D53] rounded-t hover:bg-[#8B5A3E] transition-colors cursor-default"
                        style={{ height: `${h}%` }}
                      />
                      <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 hidden group-hover:block bg-[#2A231E] text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                        {d.date}: {d.count}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="h-40 flex items-center justify-center text-gray-400 text-sm">
                No opt-in data yet
              </div>
            )}
            {data.daily_optins?.length > 0 && (
              <div className="flex justify-between text-xs text-gray-400 mt-2">
                <span>{data.daily_optins[0]?.date}</span>
                <span>{data.daily_optins[data.daily_optins.length - 1]?.date}</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Conversion Breakdown + Recent Leads */}
      <div className="grid md:grid-cols-3 gap-6">
        {/* Conversions by Tier */}
        <Card data-testid="conversion-breakdown-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Target className="h-4 w-4 text-emerald-600" />
              Conversions by Tier
            </CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(data.conversion?.tier_breakdown || {}).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(data.conversion.tier_breakdown).map(([tier, count]) => (
                  <div key={tier} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">{TIER_LABELS[tier] || tier}</span>
                    <Badge className="bg-emerald-100 text-emerald-700">{count}</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400 text-sm">
                <Target className="h-8 w-8 mx-auto mb-2 opacity-30" />
                No conversions yet
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Leads */}
        <Card className="md:col-span-2" data-testid="recent-leads-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-4 w-4 text-[#A86D53]" />
              Recent Leads
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.recent_leads?.map((lead) => (
                <div key={lead.id} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors" data-testid={`recent-lead-${lead.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm text-[#2A231E] truncate">
                        {lead.first_name || 'Anonymous'}
                      </span>
                      {lead.is_converted && (
                        <Badge className="bg-emerald-100 text-emerald-700 text-xs">Converted</Badge>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 truncate">{lead.email}</p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <Mail className="h-3 w-3" />
                      {lead.emails_sent || 0}/7
                    </div>
                    <span className="text-xs text-gray-400">
                      {lead.opted_in_at ? new Date(lead.opted_in_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                    </span>
                  </div>
                </div>
              ))}
              {(!data.recent_leads || data.recent_leads.length === 0) && (
                <div className="text-center py-8 text-gray-400 text-sm">No leads yet</div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, sub, color, bg, testId }) => (
  <Card className={`p-4 border ${bg}`} data-testid={testId}>
    <div className="flex items-start justify-between">
      <div>
        <p className={`text-2xl font-bold ${color}`}>{value}</p>
        <p className="text-xs text-gray-500 mt-0.5">{label}</p>
        {sub && <p className={`text-xs ${color} mt-0.5`}>{sub}</p>}
      </div>
      <Icon className={`h-5 w-5 ${color} opacity-60`} />
    </div>
  </Card>
);

export default AdminFunnelDashboard;
